import { Component, EventEmitter, Input, Output } from '@angular/core';
import { Icon } from '@seech/icons-ng';
import { ScopeOption } from '../../models/layout.config';

/**
 * The shell's one control for choosing which scope a subsite is acting on.
 *
 * Sites differ in what that scope is called — a Merchant here, a workspace or a
 * tenant elsewhere — so the noun arrives as `label` and nothing in this
 * component knows about any particular one. It is presentational: it renders the
 * options it is given and emits a choice. Loading the list, calling whatever
 * endpoint performs the switch and reacting to the result all stay with the site.
 *
 * It belongs to the shell rather than to a page because the scope governs every
 * page at once. A per-page copy means the same control in a different place on
 * each screen, and a switch made on one page that silently does not apply when
 * you navigate to another. It is projected into the foot of the side nav, below
 * the destinations it scopes.
 */
@Component({
  standalone: false,
  selector: 'sub-scope-switcher',
  templateUrl: './scope-switcher.component.html',
  styleUrls: ['./scope-switcher.component.scss'],
})
export class ScopeSwitcherComponent {
  /**
   * The singular noun for what is being chosen — "Merchant", "Workspace",
   * "Tenant". It labels the trigger, titles the panel and carries the
   * accessible name, so it is a noun rather than an instruction.
   */
  @Input() label = 'Workspace';
  @Input() options: ScopeOption[] = [];
  @Input() selectedId = '';
  /** Blocks interaction while a switch is in flight and shows it is working. */
  @Input() busy = false;
  @Input() icon: Icon = 'briefcase';

  /**
   * Which way the panel opens. It defaults to upward because the switcher's
   * place in the shell is the foot of the side nav, where a downward panel would
   * open off the bottom of the rail.
   */
  @Input() menuDirection: 'up' | 'down' = 'up';

  @Output() scopeChange = new EventEmitter<string>();

  get selected(): ScopeOption | null {
    return this.options.find((option) => option.id === this.selectedId) ?? null;
  }

  /**
   * Falls back to the id only when the site has a selection that is not in its
   * own list — a stale option list, or a scope the account has since lost. A
   * blank name would render an unlabelled control, which is worse than showing
   * something opaque and letting the user pick again.
   */
  get selectedName(): string {
    return this.selected?.name || this.selectedId;
  }

  /**
   * A single scope is context, not a decision: the name still shows, because
   * "which Merchant am I acting for" is worth answering even when there is only
   * one answer, but there is nothing to open.
   */
  get canSwitch(): boolean {
    return this.options.length > 1;
  }

  choose(option: ScopeOption): void {
    if (this.busy || option.id === this.selectedId) return;
    this.scopeChange.emit(option.id);
  }
}
